import { useState } from 'react';

import { Spinner } from '@blueprintjs/core';
import { Button, HorizontalTabs } from '@opendoor/mosaic';
import moment from 'moment';
import { StringParam, useQueryParam } from 'use-query-params';

import HorizontalLayout from '../components/layouts/HorizontalLayout';
import VerticalLayout from '../components/layouts/VerticalLayout';
import MaintenanceRequestList, {
  MaintenanceRequestListFilter,
} from '../components/maintenance/MaintenanceRequestList';
import TotalInvoiceableCostBadge from '../components/maintenance/TotalInvoiceableCostBadge';
import Toaster from '../components/Toaster';
import { useCreateMaintenanceInvoice } from '../graphql/mutations/web/maintenance/useCreateMaintenanceInvoice';
import useValidateInvoiceablePeriodSignature from '../graphql/queries/web/maintenance/useValidateInvoiceablePeriodSignature';
import { IS_PROD } from '../utils/constants';
import { stringDateToProto } from '../utils/date';
import NotFoundErrorPage from './NotFoundErrorPage';

const MaintenanceInvoicePage = () => {
  // start and end period query params will be in the format of YYYY-MM-DD
  const [startPeriodOn] = useQueryParam('startPeriodOn', StringParam);
  const [endPeriodOn] = useQueryParam('endPeriodOn', StringParam);
  const [orgId] = useQueryParam('organizationId', StringParam);
  const [signature] = useQueryParam('signature', StringParam);
  const [selectedTab, setSelectedTab] = useState(MaintenanceRequestListFilter.OUTSTANDING);

  const [createInvoice, { loading: createInvoiceLoading }] = useCreateMaintenanceInvoice();

  const { data, loading, error } = useValidateInvoiceablePeriodSignature({
    variables: {
      startPeriod: startPeriodOn,
      endPeriod: endPeriodOn,
      signature: signature,
      orgId: orgId,
    },
  });
  const urlIsValid = data?.webMaintenance.validateInvoiceablePeriodSignature?.validated || false;
  if (loading) {
    return <Spinner />;
  }
  if (error || !urlIsValid || !startPeriodOn || !endPeriodOn || !orgId || !signature) {
    return <NotFoundErrorPage />;
  }

  const handleCreateInvoicePress = async () => {
    if (!orgId || !startPeriodOn || !endPeriodOn) {
      Toaster.show({
        message: 'Could not find valid parameters',
        intent: 'danger',
      });
      return;
    }
    const startDateProto = stringDateToProto(startPeriodOn);
    const endDateProto = stringDateToProto(endPeriodOn);
    if (!startDateProto || !endDateProto) {
      Toaster.show({
        message: 'Could not transform string periods into dates',
        intent: 'danger',
      });
      return;
    }

    createInvoice({
      variables: {
        orgId: orgId,
        startPeriodOn: startDateProto,
        endPeriodOn: endDateProto,
      },
    })
      .then((data) => {
        if (!!data && !!data.data?.webMaintenance.createMaintenanceInvoice?.invoiceId) {
          const invoiceId = data.data.webMaintenance.createMaintenanceInvoice.invoiceId;
          const legacyUrl = IS_PROD
            ? 'https://admin.opendoor.com/vendor/invoices'
            : 'https://admin.simplersell.com/vendor/invoices';
          window.open(`${legacyUrl}/${invoiceId}`);
        }
      })
      .catch((e) => {
        Toaster.show({
          message: `There was an error creating an invoice: ${e.message}`,
          intent: 'danger',
        });
      });
  };

  const prettifiedStart = moment(startPeriodOn).format('MMM DD');
  const prettifiedEnd = moment(endPeriodOn).format('MMM DD');

  const getHeaderActionComponent = () => {
    if (selectedTab === MaintenanceRequestListFilter.APPROVED) {
      if (createInvoiceLoading) {
        return <Spinner />;
      }
      return (
        <Button
          variant="outlined"
          onClick={handleCreateInvoicePress}
          disabled={moment().isBefore(moment(endPeriodOn, 'YYYY-MM-DD'))}
        >
          Create Invoice
        </Button>
      );
    }
    return null;
  };

  const tabs = [
    {
      title: `${MaintenanceRequestListFilter.OUTSTANDING}`,
      isActive: selectedTab === MaintenanceRequestListFilter.OUTSTANDING,
      onClick: () => {
        setSelectedTab(MaintenanceRequestListFilter.OUTSTANDING);
      },
    },
    {
      title: `${MaintenanceRequestListFilter.PENDING_OPENDOOR_REVIEW}`,
      isActive: selectedTab === MaintenanceRequestListFilter.PENDING_OPENDOOR_REVIEW,
      onClick: () => {
        setSelectedTab(MaintenanceRequestListFilter.PENDING_OPENDOOR_REVIEW);
      },
    },
    {
      title: `${MaintenanceRequestListFilter.APPROVED}`,
      isActive: selectedTab === MaintenanceRequestListFilter.APPROVED,
      onClick: () => {
        setSelectedTab(MaintenanceRequestListFilter.APPROVED);
      },
    },
    {
      title: `${MaintenanceRequestListFilter.INVOICED}`,
      isActive: selectedTab === MaintenanceRequestListFilter.INVOICED,
      onClick: () => {
        setSelectedTab(MaintenanceRequestListFilter.INVOICED);
      },
    },
    {
      title: `${MaintenanceRequestListFilter.DENIED}`,
      isActive: selectedTab === MaintenanceRequestListFilter.DENIED,
      onClick: () => {
        setSelectedTab(MaintenanceRequestListFilter.DENIED);
      },
    },
  ];

  return (
    <VerticalLayout style={styles.container}>
      <HorizontalLayout style={styles.header}>
        <span style={styles.headerText}>
          Invoiceable Items: {prettifiedStart} to {prettifiedEnd}
        </span>
        {getHeaderActionComponent()}
      </HorizontalLayout>
      <HorizontalTabs links={tabs} />
      {selectedTab === MaintenanceRequestListFilter.OUTSTANDING && (
        <VerticalLayout>
          <div style={styles.oustandingDirective}>
            Below items are not yet marked complete. Please add the required pictures and submit
            once completed.
          </div>
          <MaintenanceRequestList
            organizationId={orgId || ''}
            startPeriod={startPeriodOn || ''}
            endPeriod={endPeriodOn || ''}
            filter={MaintenanceRequestListFilter.OUTSTANDING}
          />
        </VerticalLayout>
      )}
      {selectedTab === MaintenanceRequestListFilter.PENDING_OPENDOOR_REVIEW && (
        <MaintenanceRequestList
          organizationId={orgId || ''}
          startPeriod={startPeriodOn || ''}
          endPeriod={endPeriodOn || ''}
          filter={MaintenanceRequestListFilter.PENDING_OPENDOOR_REVIEW}
        />
      )}
      {selectedTab === MaintenanceRequestListFilter.APPROVED && (
        <VerticalLayout style={{ marginTop: '3vh' }}>
          <TotalInvoiceableCostBadge
            organizationId={orgId || ''}
            startPeriodOn={startPeriodOn || ''}
            endPeriodOn={endPeriodOn || ''}
          />
          <MaintenanceRequestList
            organizationId={orgId || ''}
            startPeriod={startPeriodOn || ''}
            endPeriod={endPeriodOn || ''}
            filter={MaintenanceRequestListFilter.APPROVED}
          />
        </VerticalLayout>
      )}
      {selectedTab === MaintenanceRequestListFilter.INVOICED && (
        <VerticalLayout style={{ marginTop: '3vh' }}>
          <TotalInvoiceableCostBadge
            organizationId={orgId || ''}
            startPeriodOn={startPeriodOn || ''}
            endPeriodOn={endPeriodOn || ''}
          />
          <MaintenanceRequestList
            organizationId={orgId || ''}
            startPeriod={startPeriodOn || ''}
            endPeriod={endPeriodOn || ''}
            filter={MaintenanceRequestListFilter.INVOICED}
          />
        </VerticalLayout>
      )}
      {selectedTab === MaintenanceRequestListFilter.DENIED && (
        <MaintenanceRequestList
          organizationId={orgId || ''}
          startPeriod={startPeriodOn || ''}
          endPeriod={endPeriodOn || ''}
          filter={MaintenanceRequestListFilter.DENIED}
        />
      )}
    </VerticalLayout>
  );
};

export default MaintenanceInvoicePage;

const styles = {
  container: {
    backgroundColor: 'white',
    // ensures that the background of the whole page is still white even if
    // there aren't enough maintenance requests in the table to fill the whole page
    minHeight: '94vh',
    height: '100%',
    width: '100%',
    display: 'flex',
    paddingLeft: '5vw',
    paddingRight: '5vw',
    paddingTop: '5vh',
  },
  header: {
    justifyContent: 'space-between',
    padding: '2vh',
  },
  headerText: {
    fontWeight: 600,
    fontSize: 24,
  },
  oustandingDirective: {
    borderWidth: 1,
    borderColor: '#6E6E6E',
    borderStyle: 'solid',
    borderRadius: 5,
    padding: 10,
    marginTop: '2vh',
  },
};
